import Phaser from 'phaser';
import { InputState } from './InputState';

const JOY_RADIUS = 80;
const JOY_DEADZONE = 0.3;
const JOY_VERTICAL_THRESHOLD = 0.55;
const BTN_RADIUS = 48;

type ButtonRefs = {
  arc: Phaser.GameObjects.Arc;
  baseColor: number;
};

export class TouchInput {
  private joyBase: Phaser.GameObjects.Arc;
  private joyThumb: Phaser.GameObjects.Arc;
  private joyActive = false;
  private joyPointerId = -1;
  private joyCenter = new Phaser.Math.Vector2();
  private joyVec = new Phaser.Math.Vector2();

  private hitBtn: ButtonRefs;
  private jumpBtn: ButtonRefs;
  private healBtn: ButtonRefs;
  private dashBtn: ButtonRefs;
  private spellBtn: ButtonRefs;
  private jumpHeld = false;
  private healHeld = false;

  constructor(
    private scene: Phaser.Scene,
    private state: InputState,
  ) {
    const w = scene.scale.gameSize.width;
    const h = scene.scale.gameSize.height;

    // Джойстик
    this.joyBase = scene.add
      .circle(0, 0, JOY_RADIUS, 0xffffff, 0.1)
      .setStrokeStyle(2, 0xffffff, 0.35)
      .setScrollFactor(0)
      .setDepth(1000)
      .setVisible(false);
    this.joyThumb = scene.add
      .circle(0, 0, 36, 0xffffff, 0.4)
      .setScrollFactor(0)
      .setDepth(1001)
      .setVisible(false);

    // Кнопки в правом нижнем углу:
    //   HEAL SPELL JUMP
    //        DASH  HIT
    this.healBtn = this.makeButton(w - 310, h - 220, 0x77dd99, 'HEAL');
    this.spellBtn = this.makeButton(w - 200, h - 220, 0xcc88ff, 'SPELL');
    this.jumpBtn = this.makeButton(w - 90, h - 220, 0x88aaff, 'JUMP');
    this.dashBtn = this.makeButton(w - 200, h - 100, 0xddaa66, 'DASH');
    this.hitBtn = this.makeButton(w - 90, h - 100, 0xff8866, 'HIT');

    this.bindButton(this.spellBtn, () => {
      this.state.spellPressed = true;
    });
    this.bindButton(this.hitBtn, () => {
      this.state.attackPressed = true;
    });
    this.bindButton(
      this.jumpBtn,
      () => {
        this.jumpHeld = true;
        this.state.jumpPressed = true;
      },
      () => {
        this.jumpHeld = false;
      },
    );
    this.bindButton(
      this.healBtn,
      () => {
        this.healHeld = true;
      },
      () => {
        this.healHeld = false;
      },
    );
    this.bindButton(this.dashBtn, () => {
      this.state.dashPressed = true;
    });

    this.bindJoystickPointers();
  }

  private makeButton(x: number, y: number, color: number, label: string): ButtonRefs {
    const arc = this.scene.add
      .circle(x, y, BTN_RADIUS, color, 0.4)
      .setStrokeStyle(2, 0xffffff, 0.6)
      .setScrollFactor(0)
      .setDepth(1000);
    arc.setInteractive();

    this.scene.add
      .text(x, y, label, { font: 'bold 14px monospace', color: '#ffffff' })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(1001);

    return { arc, baseColor: color };
  }

  private bindButton(btn: ButtonRefs, onPress: () => void, onRelease?: () => void) {
    btn.arc.on('pointerdown', () => {
      btn.arc.setFillStyle(btn.baseColor, 0.75);
      onPress();
    });
    const release = () => {
      btn.arc.setFillStyle(btn.baseColor, 0.4);
      if (onRelease) onRelease();
    };
    btn.arc.on('pointerup', release);
    btn.arc.on('pointerout', release);
    btn.arc.on('pointerupoutside', release);
  }

  private bindJoystickPointers() {
    const halfWidth = this.scene.scale.gameSize.width / 2;

    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.x > halfWidth) return;
      if (this.joyActive) return;

      this.joyActive = true;
      this.joyPointerId = pointer.id;
      this.joyCenter.set(pointer.x, pointer.y);
      this.joyBase.setPosition(pointer.x, pointer.y).setVisible(true);
      this.joyThumb.setPosition(pointer.x, pointer.y).setVisible(true);
    });

    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.joyActive || pointer.id !== this.joyPointerId) return;

      const dx = pointer.x - this.joyCenter.x;
      const dy = pointer.y - this.joyCenter.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const cx = dist > JOY_RADIUS ? (dx / dist) * JOY_RADIUS : dx;
      const cy = dist > JOY_RADIUS ? (dy / dist) * JOY_RADIUS : dy;

      this.joyThumb.setPosition(this.joyCenter.x + cx, this.joyCenter.y + cy);
      this.joyVec.set(cx / JOY_RADIUS, cy / JOY_RADIUS);
    });

    const release = (pointer: Phaser.Input.Pointer) => {
      if (pointer.id !== this.joyPointerId) return;
      this.joyActive = false;
      this.joyPointerId = -1;
      this.joyVec.set(0, 0);
      this.joyBase.setVisible(false);
      this.joyThumb.setVisible(false);
    };
    this.scene.input.on('pointerup', release);
    this.scene.input.on('pointerupoutside', release);
  }

  update(state: InputState) {
    if (this.joyVec.x < -JOY_DEADZONE) state.left = true;
    if (this.joyVec.x > JOY_DEADZONE) state.right = true;
    if (this.joyVec.y < -JOY_VERTICAL_THRESHOLD) state.up = true;
    if (this.joyVec.y > JOY_VERTICAL_THRESHOLD) state.down = true;

    if (this.jumpHeld) state.jumpHeld = true;
    if (this.healHeld) state.focusHeld = true;
  }
}
